import * as Sentry from "@sentry/react";
import { Userpilot } from "userpilot";
import type { IUser } from "@/types/user";
import type { IWorkspace } from "@/types/workspace";

let initialized = false;

function reportSdkError(operation: string, error: unknown): void {
  console.warn(`[userpilot] ${operation} failed`, error);
  Sentry.captureException(error, {
    tags: { source: "userpilot", operation },
  });
}

export function userpilotIsInitialized(): boolean {
  return initialized;
}

export function initUserpilot(appId: string): void {
  if (initialized) return;
  if (appId.length === 0) return;
  try {
    Userpilot.initialize(appId);
    initialized = true;
  } catch (error) {
    reportSdkError("initialize", error);
  }
}

export interface UserpilotIdentifyTraits {
  email: IUser["email"];
  display_name: IUser["display_name"];
  first_name: IUser["first_name"];
  last_name: IUser["last_name"];
  username: IUser["username"];
  date_joined: IUser["date_joined"];
  // dev and stg share the Userpilot app's Staging environment (Userpilot gives
  // an app only Staging and Production), so this is what tells them apart.
  // It rides on the company too, so company-level segments can split them.
  environment: string;
  // Discriminates next-gen (v3) records from the legacy LearnOps/Cognota
  // frontends that share this Userpilot tenant. Legacy apps never set it, so a
  // Userpilot segment on `platform = next_gen` (user- or company-level) isolates
  // next-gen analytics and flow targeting.
  platform: "next_gen";
  company: {
    id: string;
    name: string;
    slug: string;
    platform: "next_gen";
    environment: string;
  };
}

export function identifyUserpilot(user: IUser, workspace: IWorkspace): void {
  if (!initialized) return;
  const environment = import.meta.env.VITE_SENTRY_ENVIRONMENT || "local";
  const traits: UserpilotIdentifyTraits = {
    email: user.email,
    display_name: user.display_name,
    first_name: user.first_name,
    last_name: user.last_name,
    username: user.username,
    date_joined: user.date_joined,
    environment,
    platform: "next_gen",
    company: {
      id: workspace.id,
      name: workspace.name,
      slug: workspace.slug,
      platform: "next_gen",
      environment,
    },
  };
  try {
    Userpilot.identify(user.id, traits);
  } catch (error) {
    reportSdkError("identify", error);
  }
}

/**
 * Emit a custom product event to Userpilot (e.g. for flow / checklist targeting
 * and activation analytics). No-op before init, so events fired before the SDK
 * is ready (env var + LD flag) are safely dropped rather than queued or thrown.
 */
export function trackUserpilot(event: string, metadata?: Record<string, unknown>): void {
  if (!initialized) return;
  try {
    Userpilot.track(event, metadata);
  } catch (error) {
    reportSdkError("track", error);
  }
}

/**
 * Identify by id alone, with no traits, for an event that belongs to someone
 * who has just signed out (NG-2068): Userpilot keeps the traits it already
 * holds for that id.
 */
export function identifyUserpilotById(userId: string): void {
  if (!initialized) return;
  try {
    Userpilot.identify(userId);
  } catch (error) {
    reportSdkError("identify", error);
  }
}

export function anonymizeUserpilot(): void {
  if (!initialized) return;
  try {
    Userpilot.anonymous();
  } catch (error) {
    reportSdkError("anonymous", error);
  }
}

export function reloadUserpilot(): void {
  if (!initialized) return;
  try {
    Userpilot.reload();
  } catch (error) {
    reportSdkError("reload", error);
  }
}

export function __resetUserpilotForTests(): void {
  initialized = false;
}
